import { Modal, Button, Collapse } from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import DragItem from "./drag-item";
import { IColumnConfig } from "../../interfaces/common";
import { TableViewServices } from "../../services/table-view";

interface Props {
  open: boolean;
  onClose: () => void;
  configsValue: IColumnConfig[];
  onSave: (configs: IColumnConfig[]) => void;
  title?: string;
  tableName: string;
  isSave?: boolean;
  isAllCheckDisabled?: boolean;
}

const CustomizeTableModal: React.FC<Props> = ({
  open,
  onClose,
  configsValue,
  onSave,
  title = "Customise Table view",
  tableName,
  isSave = true,
  isAllCheckDisabled = false,
}) => {
  const [columnConfigs, setColumnConfigs] = useState<IColumnConfig[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setColumnConfigs(configsValue);
  }, [configsValue]);

  const areAllParentsUnchecked = (configs: IColumnConfig[]): boolean =>
    configs.every((item) => !item.isDisplay);

  const deepClone = (obj: any) => {
    if (typeof structuredClone === "function") return structuredClone(obj);
    return JSON.parse(JSON.stringify(obj));
  };

  const moveItemSameLevel = useCallback((path: number[], fromIndex: number, toIndex: number) => {
    setColumnConfigs((prev) => {
      const newConfigs: IColumnConfig[] = deepClone(prev);

      const getLevelList = (list: IColumnConfig[], p: number[]): IColumnConfig[] => {
        if (!p || p.length === 0) return list;
        const [head, ...rest] = p;
        if (!Array.isArray(list[head]?.children)) {
          list[head].children = list[head].children || [];
        }
        if (rest.length === 0) return list[head].children!;
        return getLevelList(list[head].children || [], rest);
      };

      const parentPath = path.slice(0, -1);
      const parentList = parentPath.length === 0 ? newConfigs : getLevelList(newConfigs, parentPath);

      if (!Array.isArray(parentList)) return newConfigs;
      if (fromIndex < 0 || fromIndex >= parentList.length) return newConfigs;

      const [moved] = parentList.splice(fromIndex, 1);
      parentList.splice(toIndex, 0, moved);

      return newConfigs;
    });
  }, []);

  const toggleDisplay = useCallback((path: number[]) => {
    setColumnConfigs((prev) => {
      const updateAtPath = (configs: IColumnConfig[], path: number[]): IColumnConfig[] => {
        return configs.map((item, i) => {
          if (i === path[0]) {
            if (path.length === 1) {
              return { ...item, isDisplay: item.isDisplay ? 0 : 1 };
            } else {
              return { ...item, children: updateAtPath(item.children || [], path.slice(1)) };
            }
          }
          return item;
        });
      };
      return updateAtPath(prev, path);
    });
  }, []);

  

  const isDisabledColumn = (item: IColumnConfig, parentList: IColumnConfig[] | null): boolean => {
    if (!parentList) {
      const visibleParents = columnConfigs.filter((p) => p.isDisplay === 1);
      return visibleParents.length === 1 && item.isDisplay === 1;
    } else {
      const visibleChildren = parentList.filter((c) => c.isDisplay === 1);
      return visibleChildren.length === 1 && item.isDisplay === 1;
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSave = async () => {
    setSaving(true);
    try {
      if (isSave) {
        await TableViewServices.updateCustomView(tableName, { columns: columnConfigs });
      }
      onSave(columnConfigs);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const footer = useMemo(
    () => [
      <Button key="cancel" onClick={onClose} disabled={saving}>
        Cancel
      </Button>,
      <Button
        key="save"
        type="primary"
        onClick={handleSave}
        loading={saving}
        disabled={areAllParentsUnchecked(columnConfigs)}
      >
        Save
      </Button>,
    ],
    [onClose, handleSave, saving, columnConfigs]
  );

  const renderCollapseItems = (configs: IColumnConfig[]) => {
    return configs.map((item, index) => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      const dragNode = (
        <DragItem
          id={item.column ?? `unknown-${index}`}
          index={index}
          label={item.label}
          checked={!!item.isDisplay}
          onToggle={() => toggleDisplay([index])}
          moveItem={(fromIndex: number, toIndex: number) => moveItemSameLevel([index], fromIndex, toIndex)}
          isDragDisabled={item.isDrag === 0}
          disabled={isDisabledColumn(item,null)}
          isCheckDisabled={isAllCheckDisabled}
          type="parent"
        />
      );

      return {
        key: item.column ?? `parent-${index}`,
        label: dragNode,
        children: hasChildren
          ? item.children!.map((child, cIndex) => (
              <DragItem
                key={child.column ?? `child-${index}-${cIndex}`}
                id={child.column ?? `child-${index}-${cIndex}`}
                index={cIndex}
                label={child.label}
                checked={!!child.isDisplay}
                onToggle={() => toggleDisplay([index, cIndex])}
                moveItem={(fromIndex: number, toIndex: number) =>
                  moveItemSameLevel([index, cIndex], fromIndex, toIndex)
                }
                isDragDisabled={child.isDrag === 0}
                disabled={isDisabledColumn(child,item.children!)}
                isCheckDisabled={isAllCheckDisabled}
                type="child"
              />
            ))
          : null,
        showArrow: hasChildren,
        className: hasChildren ? "" : "hide-content",
      };
    });
  };

  const items = renderCollapseItems(columnConfigs);
  const defaultActiveKey = items.map((i) => i.key);

  return (
    <Modal title={title} open={open} onCancel={onClose} width={520} footer={footer}>
      <DndProvider backend={HTML5Backend}>
        <div style={{ maxHeight: 460, overflowY: "auto", paddingRight: 8 }}>
          <Collapse
            items={items}
            defaultActiveKey={defaultActiveKey}
            bordered={false}
            expandIconPosition="end"
            className="collapse-customize"
          />
        </div>
      </DndProvider>
    </Modal>
  );
};

export default CustomizeTableModal;
